// The living Starface: the band's own logo image, cropped into its two stars and
// its mouth. The star eyes follow the pointer and blink, the mouth grins when
// something is added to the cart, and a pokeable face sticks its tongue out after five taps.
const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

class ClFace extends HTMLElement {
  #ac;
  #eyes = [];
  #queued = false;
  #px = 0;
  #py = 0;
  #blinkTimer;
  #pokes = 0;
  #pokeTimer;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.#eyes = [...this.querySelectorAll('.cl-eye')];
    this.#px = innerWidth / 2;
    this.#py = innerHeight / 3;

    if (!reduceMotion()) {
      addEventListener('pointermove', (e) => this.#look(e.clientX, e.clientY), { signal, passive: true });
      addEventListener('pointerdown', (e) => this.#look(e.clientX, e.clientY), { signal, passive: true });
      this.#scheduleBlink();
    }

    document.addEventListener('cl:added', () => this.#play('cl-grin', 750), { signal });

    if (this.hasAttribute('pokeable')) {
      this.querySelector('.cl-face-link')?.addEventListener('click', () => this.#poke(), { signal });
    }

    if (this.classList.contains('cl-intro')) {
      setTimeout(() => this.classList.remove('cl-intro'), 1600);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#blinkTimer);
    clearTimeout(this.#pokeTimer);
  }

  #look(x, y) {
    this.#px = x;
    this.#py = y;
    if (this.#queued) return;
    this.#queued = true;
    requestAnimationFrame(() => {
      this.#queued = false;
      const r = this.querySelector('.cl-face-art')?.getBoundingClientRect();
      if (!r || !r.width) return;
      for (const eye of this.#eyes) {
        // data-cx / data-cy are the star's center as a percent of the logo box.
        const ex = r.left + (Number(eye.dataset.cx) / 100) * r.width;
        const ey = r.top + (Number(eye.dataset.cy) / 100) * r.height;
        const dx = this.#px - ex;
        const dy = this.#py - ey;
        const d = Math.hypot(dx, dy) || 1;
        const m = r.width * 0.024 * Math.min(1, d / 260);
        eye.style.translate = `${((dx / d) * m).toFixed(1)}px ${((dy / d) * m).toFixed(1)}px`;
      }
    });
  }

  #scheduleBlink() {
    this.#blinkTimer = setTimeout(() => {
      if (!document.hidden) this.#play('cl-blink', 160);
      this.#scheduleBlink();
    }, 2200 + Math.random() * 3800);
  }

  #play(cls, ms) {
    if (reduceMotion()) return;
    this.classList.remove(cls);
    void this.offsetWidth;
    this.classList.add(cls);
    setTimeout(() => this.classList.remove(cls), ms);
  }

  #poke() {
    this.#pokes += 1;
    clearTimeout(this.#pokeTimer);
    this.#pokeTimer = setTimeout(() => { this.#pokes = 0; }, 1600);
    this.#play('cl-poked', 360);
    if (this.#pokes < 5) return;
    this.#pokes = 0;
    this.#play('cl-bleh', 1900);
    document.dispatchEvent(new CustomEvent('cl:sound', { detail: 'bleh' }));
    this.#confetti();
  }

  // A burst of the logo's own small star, in chalk and red.
  #confetti() {
    if (reduceMotion() || !Element.prototype.animate) return;
    const r = this.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    for (let i = 0; i < 22; i += 1) {
      const el = document.createElement('span');
      el.className = 'cl-star-mask cl-confetti';
      el.style.color = i % 3 ? 'var(--cl-chalk)' : 'var(--cl-red)';
      el.setAttribute('aria-hidden', 'true');
      document.body.append(el);
      const ang = Math.random() * Math.PI * 2;
      const dist = 120 + Math.random() * 220;
      const tx = cx + Math.cos(ang) * dist;
      const ty = cy + Math.sin(ang) * dist + 80;
      const a = el.animate([
        { transform: `translate(${cx - 14}px, ${cy - 10}px) scale(.3) rotate(0deg)`, opacity: 1 },
        { transform: `translate(${tx - 14}px, ${ty - 10}px) scale(${0.7 + Math.random()}) rotate(${Math.random() * 720 - 360}deg)`, opacity: 0 },
      ], { duration: 900 + Math.random() * 500, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' });
      a.onfinish = () => el.remove();
    }
  }
}

if (!customElements.get('cl-face')) customElements.define('cl-face', ClFace);